import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Upload, FileText, CheckCircle, XCircle, AlertCircle,
  Download, Table, ArrowRight, Loader2, X, RefreshCw,
  User, Calendar, Stethoscope, FileCheck
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BatchSubjectImport = () => {
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [mappings, setMappings] = useState({});
  const [validationResults, setValidationResults] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importComplete, setImportComplete] = useState(false);
  const [step, setStep] = useState(1); // 1: Upload, 2: Map, 3: Preview, 4: Import
  
  const fileInputRef = useRef(null);

  const requiredFields = [
    { id: 'first_name', label: 'First Name', required: true },
    { id: 'last_name', label: 'Last Name', required: true },
    { id: 'email', label: 'Email', required: true },
    { id: 'phone', label: 'Phone', required: false },
    { id: 'date_of_birth', label: 'Date of Birth', required: false },
    { id: 'age', label: 'Age', required: true },
    { id: 'condition', label: 'Condition/Study', required: true },
    { id: 'site', label: 'Site Location', required: false },
  ];

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return null;
    
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    const rows = lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
      const row = {};
      headers.forEach((h, i) => {
        row[h] = values[i] || '';
      });
      return row;
    });
    
    return { headers, rows };
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    
    if (!selectedFile.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setFile(selectedFile);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      const parsed = parseCSV(text);
      
      if (!parsed) {
        toast.error('Invalid CSV format');
        return;
      }
      
      setCsvData(parsed);
      setHeaders(parsed.headers);
      
      // Auto-map columns with similar names
      const autoMappings = {};
      requiredFields.forEach(field => {
        const match = parsed.headers.find(h => 
          h.toLowerCase().replace(/[_\s]/g, '') === field.id.toLowerCase().replace(/[_\s]/g, '') ||
          h.toLowerCase().includes(field.id.split('_')[0].toLowerCase())
        );
        if (match) {
          autoMappings[field.id] = match;
        }
      });
      setMappings(autoMappings);
      
      setStep(2);
      toast.success(`Loaded ${parsed.rows.length} rows from CSV`);
    };
    reader.readAsText(selectedFile);
  };

  const validateData = () => {
    if (!csvData) return;
    
    const results = {
      valid: [],
      errors: [],
      warnings: [],
    };
    
    csvData.rows.forEach((row, index) => {
      const rowErrors = [];
      const rowWarnings = [];
      
      // Check required fields
      requiredFields.filter(f => f.required).forEach(field => {
        const csvColumn = mappings[field.id];
        if (!csvColumn || !row[csvColumn]) {
          rowErrors.push(`Missing ${field.label}`);
        }
      });
      
      // Validate email format
      const emailColumn = mappings['email'];
      if (emailColumn && row[emailColumn]) {
        const email = row[emailColumn];
        if (!email.includes('@') || !email.includes('.')) {
          rowErrors.push('Invalid email format');
        }
      }
      
      // Validate age
      const ageColumn = mappings['age'];
      if (ageColumn && row[ageColumn]) {
        const age = parseInt(row[ageColumn]);
        if (isNaN(age) || age < 18 || age > 120) {
          rowErrors.push('Invalid age (must be 18-120)');
        }
      }
      
      // Check for potential duplicates (basic check)
      if (emailColumn && row[emailColumn]) {
        const duplicates = csvData.rows.filter((r, i) => 
          i !== index && r[emailColumn]?.toLowerCase() === row[emailColumn]?.toLowerCase()
        );
        if (duplicates.length > 0) {
          rowWarnings.push('Possible duplicate email');
        }
      }
      
      if (rowErrors.length > 0) {
        results.errors.push({ row: index + 2, data: row, errors: rowErrors });
      } else if (rowWarnings.length > 0) {
        results.warnings.push({ row: index + 2, data: row, warnings: rowWarnings });
        results.valid.push({ row: index + 2, data: row });
      } else {
        results.valid.push({ row: index + 2, data: row });
      }
    });
    
    setValidationResults(results);
    setStep(3);
  };

  const handleImport = async () => {
    if (!validationResults || validationResults.valid.length === 0) {
      toast.error('No valid records to import');
      return;
    }
    
    setImporting(true);
    
    try {
      // Transform validated data to API format
      const subjects = validationResults.valid.map(record => ({
        first_name: record.data[mappings['first_name']] || '',
        last_name: record.data[mappings['last_name']] || '',
        email: record.data[mappings['email']] || '',
        phone: record.data[mappings['phone']] || null,
        age: parseInt(record.data[mappings['age']]) || 0,
        condition: record.data[mappings['condition']] || '',
        site: record.data[mappings['site']] || 'New York Main'
      }));
      
      const response = await fetch(`${API_URL}/api/registry/subjects/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ subjects, study_id: 'DEFAULT' })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setImportComplete(true);
        setStep(4);
        toast.success(`Successfully imported ${data.imported_count} subjects`);
        
        if (data.error_count > 0) {
          toast.warning(`${data.error_count} subjects failed to import`);
        }
      } else {
        toast.error(data.detail || 'Import failed');
      }
    } catch (err) {
      console.error('Import error:', err);
      toast.error('Failed to import subjects');
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setFile(null);
    setCsvData(null);
    setHeaders([]);
    setMappings({});
    setValidationResults(null);
    setImportComplete(false);
    setStep(1);
  };

  const downloadTemplate = () => {
    const template = 'first_name,last_name,email,phone,age,condition,site\nJohn,Doe,john.doe@email.com,555-123-4567,45,Type 2 Diabetes,New York Main\nJane,Smith,jane.smith@email.com,555-987-6543,52,Hypertension,Brooklyn Clinic';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'subject_import_template.csv';
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  return (
    <div className="space-y-6" data-testid="batch-import-container">
      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-4" data-testid="batch-import-steps">
        {[
          { num: 1, label: 'Upload CSV' },
          { num: 2, label: 'Map Columns' },
          { num: 3, label: 'Preview & Validate' },
          { num: 4, label: 'Import' },
        ].map((s, idx) => (
          <div key={s.num} className="flex items-center">
            <div className={`flex items-center gap-2 ${step >= s.num ? 'text-[#1877F2]' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step > s.num ? 'bg-green-500 text-white' :
                step === s.num ? 'bg-[#1877F2] text-white' :
                'bg-gray-200 text-gray-500'
              }`}>
                {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
              </div>
              <span className="text-sm font-medium hidden md:inline">{s.label}</span>
            </div>
            {idx < 3 && (
              <ArrowRight className={`w-4 h-4 mx-2 ${step > s.num ? 'text-green-500' : 'text-gray-300'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Upload className="w-5 h-5 mr-2 text-[#1877F2]" />
              Upload CSV File
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 mb-2">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Upload a CSV file with subject data. Required columns: First Name, Last Name, Email, Age, Condition.
              </p>
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-btn">
                <Download className="w-4 h-4 mr-1" />
                Download Template
              </Button>
            </div>

            <div
              className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-[#1877F2] transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-600 mb-2">
                Click to select a CSV file or drag and drop
              </p>
              <p className="text-xs text-gray-400">
                Supports .csv files up to 10MB
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Column Mapping */}
      {step === 2 && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center">
                <Table className="w-5 h-5 mr-2 text-[#1877F2]" />
                Map CSV Columns
              </CardTitle>
              <Button variant="outline" size="sm" onClick={resetImport}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Start Over
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">
                <CheckCircle className="w-4 h-4 inline mr-1" />
                Loaded <strong>{file?.name}</strong> with {csvData?.rows.length} rows and {headers.length} columns.
              </p>
            </div>

            <div className="space-y-3">
              {requiredFields.map(field => (
                <div key={field.id} className="flex items-center gap-4">
                  <div className="w-40">
                    <span className="text-sm font-medium text-gray-700">{field.label}</span>
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                  <select
                    value={mappings[field.id] || ''}
                    onChange={(e) => setMappings({ ...mappings, [field.id]: e.target.value })}
                    className="flex-1 px-3 py-2 border rounded-lg bg-white"
                  >
                    <option value="">-- Select Column --</option>
                    {headers.map(h => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                  {mappings[field.id] && (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button 
                className="bg-[#1877F2] hover:bg-[#166FE5]"
                onClick={validateData}
                disabled={!requiredFields.filter(f => f.required).every(f => mappings[f.id])}
              >
                Validate Data
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Preview & Validate */}
      {step === 3 && validationResults && (
        <div className="space-y-4">
          {/* Validation Summary */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-0 shadow-sm bg-green-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="text-2xl font-bold text-green-700">{validationResults.valid.length}</p>
                    <p className="text-xs text-green-600">Valid Records</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-sm bg-orange-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-8 h-8 text-orange-600" />
                  <div>
                    <p className="text-2xl font-bold text-orange-700">{validationResults.warnings.length}</p>
                    <p className="text-xs text-orange-600">Warnings</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-sm bg-red-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <XCircle className="w-8 h-8 text-red-600" />
                  <div>
                    <p className="text-2xl font-bold text-red-700">{validationResults.errors.length}</p>
                    <p className="text-xs text-red-600">Errors (Will Skip)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Error Details */}
          {validationResults.errors.length > 0 && (
            <Card className="border-0 shadow-sm border-l-4 border-l-red-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium text-red-700">
                  Rows with Errors (Will Not Import)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {validationResults.errors.slice(0, 10).map((err) => (
                    <div key={`error-row-${err.row}`} className="flex items-start gap-3 p-2 bg-red-50 rounded">
                      <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded">
                        Row {err.row}
                      </span>
                      <div className="text-sm text-red-700">
                        {err.errors.join(', ')}
                      </div>
                    </div>
                  ))}
                  {validationResults.errors.length > 10 && (
                    <p className="text-xs text-red-500">+{validationResults.errors.length - 10} more errors</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Preview Table */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium text-gray-700">
                  Preview Valid Records
                </CardTitle>
                <Button variant="outline" size="sm" onClick={() => setStep(2)}>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Re-map Columns
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Row</th>
                      {requiredFields.filter(f => mappings[f.id]).map(field => (
                        <th key={field.id} className="px-3 py-2 text-left font-medium text-gray-600">
                          {field.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {validationResults.valid.slice(0, 10).map((record) => (
                      <tr key={`valid-row-${record.row}`} className="border-b border-gray-100">
                        <td className="px-3 py-2 text-gray-500">{record.row}</td>
                        {requiredFields.filter(f => mappings[f.id]).map(field => (
                          <td key={field.id} className="px-3 py-2">
                            {record.data[mappings[field.id]] || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {validationResults.valid.length > 10 && (
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Showing 10 of {validationResults.valid.length} valid records
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4 mt-4 border-t">
                <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                <Button 
                  className="bg-[#1877F2] hover:bg-[#166FE5]"
                  onClick={handleImport}
                  disabled={validationResults.valid.length === 0}
                >
                  Import {validationResults.valid.length} Subjects
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 4: Import Complete */}
      {step === 4 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-8 text-center">
            {importing ? (
              <>
                <Loader2 className="w-16 h-16 mx-auto mb-4 text-[#1877F2] animate-spin" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Importing Subjects...</h3>
                <p className="text-gray-500">Please wait while we process your data.</p>
              </>
            ) : (
              <>
                <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Import Complete!</h3>
                <p className="text-gray-500 mb-6">
                  Successfully imported {validationResults?.valid.length} subjects into the system.
                </p>
                <div className="flex justify-center gap-3">
                  <Button variant="outline" onClick={resetImport}>
                    <Upload className="w-4 h-4 mr-1" />
                    Import More
                  </Button>
                  <Button className="bg-[#1877F2] hover:bg-[#166FE5]">
                    <User className="w-4 h-4 mr-1" />
                    View Subjects
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BatchSubjectImport;
